
'use client';
import { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Button } from "@/components/ui/button";
import Image from "next/image";
import { Briefcase, Wallet, Settings, X, ListTodo, Mail, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useCurrentUser } from "@/hooks/use-current-user";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

export const Sidebar = () => {
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();

  const user = useCurrentUser();
  const nameToUse = user?.name ?? "John Doe";
  const initials = nameToUse
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  let displayType: string;
  if (user?.role === "ADMIN") {
    displayType = "Admin";
  } else if (user?.type === "CUSTOMER") {
    displayType = "Client";
  } else if (user?.type === "DRIVER") {
    displayType = "Driver";
  } else {
    displayType = "Driver";
  }

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const isDriver = user?.type === 'DRIVER';
  const navItems = [
    {
      name: "Dashboard",
      path: "/",
      icon: <Briefcase size={20} />,
    },
    {
      name: "Jobs",
      path: "/jobs",
      icon: <ListTodo size={20} />,
    },
    ...(isDriver ? [{ name: 'Map', path: '/driver/map', icon: <ListTodo size={20} /> }] : []),
    {
      name: "My Jobs",
      path: "/my-jobs",
      icon: <ListTodo size={20} />,
    },
    {
      name: "Wallet",
      path: "/wallet",
      icon: <Wallet size={20} />,
    },
    {
      name: "Settings",
      path: "/settings",
      icon: <Settings size={20} />,
    },
    {
      name: "Contact",
      path: "/contact",
      icon: <Mail size={20} />,
    },
  ];

  const closeSidebarIfMobile = () => {
    if (isMobile) {
      setIsOpen(false);
    }
  };

  return (
    <>
      {/* Mobile Toggle Button */}
      <div className="fixed z-10 top-4 left-4 md:hidden">
        <Button
          variant="outline"
          size="icon"
          onClick={toggleSidebar}
          className="bg-background"
        >
          {isOpen ? <X size={20} /> : <ChevronRight size={20} />}
        </Button>
      </div>
      
      {/* Overlay for mobile */}
      {isOpen && isMobile && (
        <div 
          className="fixed inset-0 bg-black/20 z-20"
          onClick={() => setIsOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <div
        className={cn(
          "fixed top-0 bottom-0 left-0 z-30 w-64 bg-sidebar border-r border-border p-6 transition-transform duration-300 ease-in-out md:translate-x-0",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex flex-col items-center justify-center mb-8">
            <Image
              src="/logo.svg"
              alt="Smart Taurus Logo"
              width={150}
              height={50}
              className="object-contain"
            />
          </div>
          
          {/* Nav Links */}
          <nav className="space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.name}
                href={item.path}
                onClick={closeSidebarIfMobile}
                className={cn(
                  "nav-link",
                  pathname === item.path && "active"
                )}
              >
                {item.icon}
                <span>{item.name}</span>
              </Link>
            ))}
          </nav>
          
          {/* Driver Profile */}
          <div className="mt-auto pt-6 border-t">
            <div className="flex items-center space-x-3">
              <Avatar>
                {user?.image && <AvatarImage src={user.image} alt={nameToUse} />}
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium">{nameToUse}</p>
                <p className="text-sm text-black">
                  {displayType}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Content Padding for sidebar on larger screens */}
      <div className="hidden md:block w-64" />
    </>
  );
};

export default Sidebar;
